import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, ListChecks, CheckCircle2, ClipboardList, ArrowRight } from "lucide-react";
import { TopBar } from "../components/TopBar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { formatCaseNumber } from "../lib/caseFormat";
import CaseStatusPill from "../components/case/CaseStatusPill";

const formatRelative = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 flex items-center gap-4">
    <div className={`h-11 w-11 rounded-md flex items-center justify-center ${accent}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div>
      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
      <p className="font-display text-3xl tracking-tight font-medium text-[#0A2540]">{value}</p>
    </div>
  </div>
);

const ACTIVE_STATUSES = ["under_review"];
const QUEUE_FILTERS = [
  { v: "active",   k: "reviewer_filter_active" },
  { v: "approved", k: "reviewer_filter_approved" },
  { v: "paid",     k: "reviewer_filter_paid" },
  { v: "rejected", k: "reviewer_filter_rejected" },
  { v: "closed",   k: "reviewer_filter_closed" },
  { v: "all",      k: "reviewer_filter_all" },
];

const ReviewerDashboard = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [tab, setTab] = useState("pool");
  const [pool, setPool] = useState([]);
  const [queue, setQueue] = useState([]);
  const [queueFilter, setQueueFilter] = useState("active");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [p, q] = await Promise.all([
        api.get("/cases/pool"),
        // Phase-9 bug fix: queue now returns ALL assigned cases regardless of status.
        api.get("/cases/queue"),
      ]);
      setPool(p.data);
      setQueue(q.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_generic"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closedThisWeek = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 3600 * 1000;
    return queue.filter(
      (c) => c.status === "closed" && c.closed_at && new Date(c.closed_at).getTime() >= cutoff
    ).length;
  }, [queue]);

  const filterCounts = useMemo(() => {
    const c = { active: 0, approved: 0, paid: 0, rejected: 0, closed: 0, all: queue.length };
    for (const it of queue) {
      if (ACTIVE_STATUSES.includes(it.status)) c.active += 1;
      else if (it.status === "approved") c.approved += 1;
      else if (it.status === "paid") c.paid += 1;
      else if (it.status === "rejected") c.rejected += 1;
      else if (it.status === "closed") c.closed += 1;
    }
    return c;
  }, [queue]);

  const filteredQueue = useMemo(() => {
    if (queueFilter === "all") return queue;
    if (queueFilter === "active") return queue.filter((c) => ACTIVE_STATUSES.includes(c.status));
    return queue.filter((c) => c.status === queueFilter);
  }, [queue, queueFilter]);

  const handleClaim = async (id) => {
    try {
      await api.post(`/cases/${id}/claim`);
      toast.success(t("toast_claimed"));
      navigate(`/cases/${id}`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) {
        toast.error(t("err_already_claimed"));
        load();
      } else {
        toast.error(detail || t("err_generic"));
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="reviewer-dashboard">
      <TopBar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6 animate-fade-in">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold mb-1">
            Reviewer · {user?.name}
          </p>
          <h2 className="font-display text-3xl tracking-tight font-medium text-[#0A2540]">
            {t("reviewer_heading")}
          </h2>
          <p className="text-slate-500 text-sm mt-1">{t("reviewer_subheading")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatCard
            icon={Inbox}
            label={t("reviewer_stat_pool")}
            value={pool.length}
            accent="bg-blue-50 text-blue-700"
          />
          <StatCard
            icon={ClipboardList}
            label={t("reviewer_stat_queue")}
            value={queue.length}
            accent="bg-amber-50 text-amber-700"
          />
          <StatCard
            icon={CheckCircle2}
            label={t("reviewer_stat_closed_week")}
            value={closedThisWeek}
            accent="bg-emerald-50 text-emerald-700"
          />
        </div>

        <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
          <div className="px-4 sm:px-6 py-4 border-b border-slate-200 flex items-center gap-2">
            {[
              { v: "pool", l: t("reviewer_tab_pool"), icon: Inbox, count: pool.length },
              { v: "queue", l: t("reviewer_tab_queue"), icon: ListChecks, count: queue.length },
            ].map((tabDef) => {
              const active = tab === tabDef.v;
              const Icon = tabDef.icon;
              return (
                <button
                  key={tabDef.v}
                  type="button"
                  onClick={() => setTab(tabDef.v)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors focus-ring inline-flex items-center gap-2 ${
                    active
                      ? "bg-[#0A2540] text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`reviewer-tab-${tabDef.v}`}
                >
                  <Icon className="h-4 w-4" />
                  {tabDef.l}
                  <span
                    className={`ml-1 px-1.5 py-0.5 rounded text-xs ${
                      active ? "bg-white/20 text-white" : "bg-white text-slate-600"
                    }`}
                  >
                    {tabDef.count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="overflow-x-auto">
            {tab === "pool" ? (
              <table className="w-full" data-testid="reviewer-pool-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    <th className="px-6 py-3 text-left w-24">{t("case_number_label")}</th>
                    <th className="px-6 py-3 text-left">{t("reviewer_table_applicant")}</th>
                    <th className="px-6 py-3 text-left">{t("reviewer_table_agent")}</th>
                    <th className="px-6 py-3 text-left">{t("reviewer_table_submitted")}</th>
                    <th className="px-6 py-3 text-left">{t("reviewer_table_type")}</th>
                    <th className="px-6 py-3 text-right">{t("reviewer_table_actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center">
                        <div className="inline-block h-6 w-6 border-2 border-slate-300 border-t-[#0A2540] rounded-full animate-spin" />
                      </td>
                    </tr>
                  ) : pool.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-10 py-16 text-center text-slate-500" data-testid="reviewer-pool-empty">
                        <Inbox className="h-8 w-8 mx-auto text-slate-300 mb-3" />
                        <p className="text-sm">{t("reviewer_pool_empty")}</p>
                      </td>
                    </tr>
                  ) : (
                    pool.map((c) => (
                      <tr
                        key={c.id}
                        className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                        data-testid={`pool-row-${c.id}`}
                      >
                        <td className="px-6 py-4 align-middle text-sm font-mono text-[#0A2540]" data-testid={`pool-case-number-${c.id}`}>
                          {formatCaseNumber(c.case_number)}
                        </td>
                        <td className="px-6 py-4 align-middle text-sm font-medium text-[#0A2540]">
                          {c.applicant_name || (
                            <span className="text-slate-400 italic">
                              {t("case_form_new_title")}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 align-middle text-sm text-slate-500">
                          {c.agent_name || "—"}
                        </td>
                        <td className="px-6 py-4 align-middle text-sm text-slate-500">
                          {formatRelative(c.submitted_at)}
                        </td>
                        <td className="px-6 py-4 align-middle">
                          <CaseStatusPill status={c.status} />
                        </td>
                        <td className="px-6 py-4 align-middle text-right">
                          <Button
                            size="sm"
                            onClick={() => handleClaim(c.id)}
                            className="bg-[#0A2540] hover:bg-[#051626] text-white"
                            data-testid={`claim-case-${c.id}`}
                          >
                            {t("reviewer_action_claim")}
                            <ArrowRight className="h-4 w-4 ml-1" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : (
              <>
                {/* Phase-9 bug fix: queue filter chips */}
                <div
                  className="px-4 sm:px-6 py-3 border-b border-slate-200 flex flex-wrap gap-2"
                  data-testid="queue-filter-chips"
                >
                  {QUEUE_FILTERS.map((f) => {
                    const active = queueFilter === f.v;
                    return (
                      <button
                        key={f.v}
                        type="button"
                        onClick={() => setQueueFilter(f.v)}
                        data-testid={`queue-filter-${f.v}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors focus-ring ${
                          active
                            ? "bg-[#0A2540] text-white border-[#0A2540]"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        {t(f.k)}
                        <span
                          className={`ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] px-1 rounded text-[10px] ${
                            active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {filterCounts[f.v]}
                        </span>
                      </button>
                    );
                  })}
                </div>
              <table className="w-full" data-testid="reviewer-queue-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    <th className="px-6 py-3 text-left w-24">{t("case_number_label")}</th>
                    <th className="px-6 py-3 text-left">{t("reviewer_table_applicant")}</th>
                    <th className="px-6 py-3 text-left">{t("reviewer_table_status")}</th>
                    <th className="px-6 py-3 text-left">{t("reviewer_table_activity")}</th>
                    <th className="px-6 py-3 text-right">{t("reviewer_table_actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center">
                        <div className="inline-block h-6 w-6 border-2 border-slate-300 border-t-[#0A2540] rounded-full animate-spin" />
                      </td>
                    </tr>
                  ) : filteredQueue.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-10 py-16 text-center text-slate-500" data-testid="reviewer-queue-empty">
                        <ClipboardList className="h-8 w-8 mx-auto text-slate-300 mb-3" />
                        <p className="text-sm">{t("reviewer_queue_empty")}</p>
                      </td>
                    </tr>
                  ) : (
                    filteredQueue.map((c) => (
                      <tr
                        key={c.id}
                        className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                        data-testid={`queue-row-${c.id}`}
                      >
                        <td className="px-6 py-4 align-middle text-sm font-mono text-[#0A2540]" data-testid={`queue-case-number-${c.id}`}>
                          {formatCaseNumber(c.case_number)}
                        </td>
                        <td className="px-6 py-4 align-middle text-sm font-medium text-[#0A2540]">
                          {c.applicant_name || (
                            <span className="text-slate-400 italic">
                              {t("case_form_new_title")}
                            </span>
                          )}
                          <p className="text-xs text-slate-500 font-normal mt-0.5">{c.agent_name}</p>
                        </td>
                        <td className="px-6 py-4 align-middle">
                          <CaseStatusPill status={c.status} />
                        </td>
                        <td className="px-6 py-4 align-middle text-sm text-slate-500">
                          {formatRelative(c.updated_at)}
                        </td>
                        <td className="px-6 py-4 align-middle text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/cases/${c.id}`)}
                            className="text-[#0A2540] hover:text-[#007AFF]"
                            data-testid={`open-queue-${c.id}`}
                          >
                            {t("reviewer_action_open")}
                            <ArrowRight className="h-4 w-4 ml-1" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ReviewerDashboard;
